import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => ({
  quote: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    borderLeft: `3px solid ${theme.palette.divider}`,
    background: theme.palette.action.hover,
    fontSize: 13.5,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    marginBottom: theme.spacing(2),
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  analyzing: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(3, 0),
    justifyContent: "center",
  },
  spark: {
    display: "inline-block",
    fontSize: 18,
    color: theme.palette.primary.main,
    animation: "coachSpark 1.8s ease-in-out infinite",
    "@keyframes coachSpark": {
      "0%": { transform: "rotate(0deg) scale(0.85)", opacity: 0.6 },
      "50%": { transform: "rotate(180deg) scale(1.15)", opacity: 1 },
      "100%": { transform: "rotate(360deg) scale(0.85)", opacity: 0.6 },
    },
  },
  diagnosis: {
    fontSize: 13.5,
    lineHeight: 1.55,
    marginBottom: theme.spacing(2),
  },
  targetRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  edit: {
    marginBottom: theme.spacing(2),
  },
  reason: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
  },
  block: {
    fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
    fontSize: 12,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 6,
    maxHeight: 220,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  removed: {
    background: theme.palette.mode === "dark" ? "rgba(248,81,73,0.15)" : "rgba(248,81,73,0.10)",
    borderLeft: `3px solid ${theme.palette.error.main}`,
    marginBottom: theme.spacing(0.5),
  },
  added: {
    background: theme.palette.mode === "dark" ? "rgba(46,160,67,0.15)" : "rgba(46,160,67,0.10)",
    borderLeft: `3px solid ${theme.palette.success.main}`,
  },
}));

const CoachDialog = ({ open, onClose, promptId, draftPrompt, messages, correctedIndex, onApplyToDraft, onSavedSpecialist }) => {
  const { classes } = useStyles();
  const [correction, setCorrection] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCorrection("");
    setProposal(null);
    setLoading(false);
    setSaving(false);
  }, [open, correctedIndex]);

  const corrected = messages[correctedIndex];

  const analyze = async () => {
    if (!correction.trim()) return;
    setLoading(true);
    setProposal(null);
    try {
      const { data } = await api.post(`/prompt/${promptId}/coach`, {
        borrador: draftPrompt,
        mensajes: messages.map((message) => ({
          role: message.role === "user" && message.trainer ? "system" : message.role,
          text: message.text,
          trace: message.trace || [],
        })),
        indice: correctedIndex,
        correccion: correction.trim(),
      });
      setProposal(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!proposal) return;
    if (proposal.target.isSelf) {
      onApplyToDraft(proposal.newPrompt, proposal.versionNote);
      onClose();
      return;
    }
    setSaving(true);
    try {
      await api.put(`/prompt/${proposal.target.promptId}`, {
        prompt: proposal.newPrompt,
        versionNote: proposal.versionNote,
      });
      onSavedSpecialist(proposal.target.name);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading || saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{i18n.t("promptCoach.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography className={classes.label}>{i18n.t("promptCoach.reply")}</Typography>
        <div className={classes.quote}>{corrected?.text}</div>

        {!proposal && (
          <TextField
            label={i18n.t("promptCoach.correctionLabel")}
            placeholder={i18n.t("promptCoach.correctionPlaceholder")}
            fullWidth
            multiline
            minRows={3}
            autoFocus
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            disabled={loading}
          />
        )}

        {loading && (
          <div className={classes.analyzing}>
            <span className={classes.spark}>✻</span>
            <Typography>{i18n.t("promptCoach.analyzing")}</Typography>
          </div>
        )}

        {proposal && (
          <>
            <Typography className={classes.label}>{i18n.t("promptCoach.correctionLabel")}</Typography>
            <Typography className={classes.diagnosis} color="text.secondary">
              {correction}
            </Typography>

            <Typography className={classes.label}>{i18n.t("promptCoach.diagnosis")}</Typography>
            <Typography className={classes.diagnosis}>{proposal.diagnosis}</Typography>

            <div className={classes.targetRow}>
              <Typography className={classes.label} style={{ marginBottom: 0 }}>
                {i18n.t("promptCoach.target")}
              </Typography>
              <Chip
                size="small"
                color={proposal.target.isSelf ? "primary" : "secondary"}
                variant="outlined"
                label={
                  proposal.target.isSelf
                    ? i18n.t("promptCoach.targetSelf", { name: proposal.target.name })
                    : i18n.t("promptCoach.targetSpecialist", { name: proposal.target.name })
                }
              />
            </div>

            {proposal.edits.map((edit, index) => (
              <div key={index} className={classes.edit}>
                <Typography className={classes.reason}>
                  {index + 1}. {edit.reason}
                </Typography>
                <div className={`${classes.block} ${classes.removed}`}>{edit.find}</div>
                <div className={`${classes.block} ${classes.added}`}>{edit.replace}</div>
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || saving}>
          {i18n.t("promptCoach.cancel")}
        </Button>
        {proposal && (
          <Button onClick={() => setProposal(null)} disabled={saving}>
            {i18n.t("promptCoach.retry")}
          </Button>
        )}
        {!proposal ? (
          <Button
            variant="contained"
            startIcon={<AutoFixHighOutlinedIcon fontSize="small" />}
            onClick={analyze}
            disabled={!correction.trim() || loading}
          >
            {i18n.t("promptCoach.analyze")}
          </Button>
        ) : (
          <Button variant="contained" onClick={apply} disabled={saving}>
            {proposal.target.isSelf
              ? i18n.t("promptCoach.applyDraft")
              : i18n.t("promptCoach.saveSpecialist", { name: proposal.target.name })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CoachDialog;
